import { format } from "date-fns";
import clamp from "lodash/clamp";
import { DATE_FORMAT, parseInputDate } from "~/hooks/search/constants";
import { validateCompleteDateRange } from "~/hooks/search/date-range-validation";
import {
  AGE_TYPE_BOUNDS,
  Filter,
  FilterType,
  ValidAgeRangeFilterSchema,
  ValidCheckboxFilterSchema,
  ValidNumericFilterSchema,
} from "~/hooks/search/types";
import { TFunction } from "i18next";
import { AgeRangeAvailableBounds, AgeType } from "reedy-data/api";
import { isActiveSalesDataFilter } from "~/hooks/search/sales-data-filter-utils";

export const defaultRange = { from: "", to: "" };

export const formatAgeValue = (
  ageType: AgeType,
  value: number,
  t: TFunction,
): string =>
  ageType === AgeType.INTEREST_AGE_MONTHS
    ? t("books:filters.ageRange.months", { count: value })
    : t("books:filters.ageRange.years", { count: value });

const AGE_TYPE_TO_BOUNDS_KEY: Record<AgeType, keyof AgeRangeAvailableBounds> = {
  [AgeType.INTEREST_AGE_MONTHS]: "interest_age_months",
  [AgeType.INTEREST_AGE_YEARS]: "interest_age_years",
  [AgeType.READING_AGE_YEARS]: "reading_age_years",
};

// A null bound means no product carries that age type, so filtering on it
// returns nothing. A missing bounds object only means "not answered yet".
export const isAgeTypeAvailable = (
  ageType: AgeType,
  availableBounds: AgeRangeAvailableBounds | null | undefined,
): boolean =>
  !availableBounds || Boolean(availableBounds[AGE_TYPE_TO_BOUNDS_KEY[ageType]]);

// ONIX caps when alone; otherwise backend available bounds clamped to caps.
export const getEffectiveAgeBounds = (
  ageType: AgeType,
  availableBounds: AgeRangeAvailableBounds | null | undefined,
  hasOtherActiveFilters: boolean,
): { min: number; max: number } => {
  const caps = AGE_TYPE_BOUNDS[ageType];
  if (!hasOtherActiveFilters) return caps;

  const available = availableBounds?.[AGE_TYPE_TO_BOUNDS_KEY[ageType]];
  if (!available) return caps;

  const min = clamp(available.min, caps.min, caps.max);
  const max = clamp(available.max, caps.min, caps.max);
  // Equal bounds are valid (a single available age); only guard inverted data.
  return min <= max ? { min, max } : caps;
};

export const isValidFilter = (
  filter: Filter | null | undefined,
  salesDataMaxDate: Date,
): boolean => {
  if (filter?.type === FilterType.SALES_DATA) {
    return isActiveSalesDataFilter(filter, salesDataMaxDate);
  }
  if (filter?.type === FilterType.DATE_RANGE) {
    return Boolean(
      filter.range?.from &&
        filter.range.to &&
        validateCompleteDateRange(filter.range).isValid,
    );
  }
  return [
    ValidNumericFilterSchema,
    ValidCheckboxFilterSchema,
    ValidAgeRangeFilterSchema,
  ].some((schema) => schema.safeParse(filter).success);
};

export const getNoOfValidFilters = (
  filters: Record<string, Filter | null | undefined>,
): number => {
  const salesDataMaxDate = new Date();
  return Object.entries(filters).filter(
    ([, filter]) => filter && isValidFilter(filter, salesDataMaxDate),
  ).length;
};

export const transformToInputDate = (date: Date) => {
  return format(date, DATE_FORMAT);
};

export const parseDateRange = (dateRange: { from?: string; to?: string }) => {
  return {
    from: dateRange.from ? parseInputDate(dateRange.from) : undefined,
    to: dateRange.to ? parseInputDate(dateRange.to) : undefined,
  };
};

export const getErrorMessage = (
  dateRange: {
    from: string;
    to: string;
  },
  t: TFunction,
): string => {
  const validation = validateCompleteDateRange(dateRange);
  return validation.errorKey
    ? t(`books:filters.errors.${validation.errorKey}`)
    : "";
};
